import { Array, Boolean, Guard, Literal, Null, Number, Record, Static, String, Union } from 'runtypes';

import { ActivityLinkButtonRunType, ActivityLinkRunType } from 'src/models/activityLink';
import { ArchivButtonRunType } from 'src/models/archiv';
import { GlobalizationButtonRunType } from 'src/models/globalization';
import { IdoButtonRunType } from 'src/models/ido';
import { SpoingButtonRunType } from 'src/models/spoing';
import { StatusButtonRunType } from 'src/models/status';
import { type ModuleLayout as _ModuleLayout, MODULE_LAYOUT_VALUES } from 'src/models/task/enums';

const ModuleLayoutRunType = Guard(
	(value: unknown): value is _ModuleLayout =>
		typeof value === 'string' && MODULE_LAYOUT_VALUES.includes(value as _ModuleLayout)
);

const TaskLockConfigRunType = Record({
	aggregateId: String,
	aggregateVersion: Number,
	date: String,
	locked: Boolean,
	processId: String,
	userDisplayName: String,
	userId: Number,
	userShortcut: String,
});

export const TaskAlarmConfigNotifiableUserRunType = Record({
	aggregateId: String,
	aggregateVersion: Number,
	date: String,
	notified: Boolean,
	processId: String,
	userDisplayname: String,
});

export type TaskAlarmConfigNotifiableUser = Static<typeof TaskAlarmConfigNotifiableUserRunType>;

export const TaskAlarmConfigRunType = Record({
	aggregateId: String,
	aggregateVersion: Number,
	alarmConfigNotifiableUsers: Array(TaskAlarmConfigNotifiableUserRunType),
	date: String,
	notifiable: Boolean,
	ownerDate: String,
	ownerNotified: Boolean,
	ownerType: Literal('never').Or(Literal('always')).Or(Literal('onlyOnUnfinished')),
	processId: String,
});

export type TaskAlarmConfig = Static<typeof TaskAlarmConfigRunType>;

export const TaskRepetitionEndRunType = Literal('never').Or(Literal('onFixedDate')).Or(Literal('byNumber'));

export type TaskRepetitionEnd = Static<typeof TaskRepetitionEndRunType>;

export const TaskRepetitionMonthlyRuleRunType = Literal('byDay').Or(Literal('byWeekday'));

export type TaskRepetitionMonthlyRule = Static<typeof TaskRepetitionMonthlyRuleRunType>;

export const TaskRepetitionTypeRunType = Literal('none')
	.Or(Literal('daily'))
	.Or(Literal('weekly'))
	.Or(Literal('monthly'))
	.Or(Literal('yearly'));

export type TaskRepetitionType = Static<typeof TaskRepetitionTypeRunType>;

const TaskRepetitionWeekDaySelectorRecord = {
	weekDaySelectorFriday: Boolean,
	weekDaySelectorMonday: Boolean,
	weekDaySelectorSaturday: Boolean,
	weekDaySelectorSunday: Boolean,
	weekDaySelectorThursday: Boolean,
	weekDaySelectorTuesday: Boolean,
	weekDaySelectorWednesday: Boolean,
};

export const TaskRepetitionConfigRunType = Record({
	aggregateId: String,
	aggregateVersion: Number,
	countEnd: Number,
	cycleTime: Number,
	end: TaskRepetitionEndRunType,
	fixedEndDate: String,
	monthlyRule: TaskRepetitionMonthlyRuleRunType,
	processId: String,
	type: TaskRepetitionTypeRunType,
	...TaskRepetitionWeekDaySelectorRecord,
});

export type TaskRepetitionConfig = Static<typeof TaskRepetitionConfigRunType>;

const TaskFavoriteButtonRunType = Record({
	aggregateId: String,
	aggregateVersion: Number,
	changeable: Boolean,
	favored: Boolean,
	processId: String,
	render: Boolean,
});

const TaskHistoryFragmentRunType = Record({
	aggregateId: String,
	aggregateVersion: Number,
	date: String,
	key: String,
	processId: String,
	userShortcut: String,
	value: String,
});

const TaskHistoryEntriesRunType = Record({
	aggregateId: String,
	aggregateVersion: Number,
	historyFragments: Array(TaskHistoryFragmentRunType),
	processId: String,
	userShortcut: String,
});

export const TaskAvailableUsersRunType = Record({
	Displayname: String,
	ID: Number,
	UserAvatar: String,
	UserDisplayname: String,
});

export type TaskAvailableUsers = Static<typeof TaskAvailableUsersRunType>;

const TaskAllUsersRunType = Record({
	Displayname: String,
	ID: Number,
	UserAvatar: String,
	UserDisplayname: String,
});

export const TaskButtonRunType = Record({
	activityLink: ActivityLinkRunType,
	aggregateId: String,
	aggregateVersion: Number,
	contact: String,
	contactIsBase64Encoded: Boolean,
	content: Union(String, Null),
	contentIsBase64Encoded: Boolean,
	moduleLayout: ModuleLayoutRunType,
	originTimestamp: String.Or(Number),
	processId: String,
	responsibleUserId: Number.Or(String),
	title: String.Or(Null),
	titleIsBase64Encoded: Boolean,
});

export const TaskRunType = Record({
	activitylinkButton: ActivityLinkButtonRunType,
	aggregateId: String,
	aggregateVersion: Number,
	alarmConfig: TaskAlarmConfigRunType,
	allUsers: Array(TaskAllUsersRunType),
	archivButton: ArchivButtonRunType,
	availableUsers: Array(TaskAvailableUsersRunType),
	changeable: Boolean,
	content: String,
	favoriteButton: TaskFavoriteButtonRunType,
	fixedMaturityDate: String,
	forerunTime: Number,
	globalizationButton: GlobalizationButtonRunType,
	historyEntries: Array(TaskHistoryEntriesRunType),
	idoButton: IdoButtonRunType,
	inactive: Boolean,
	lockConfig: TaskLockConfigRunType,
	moduleLayout: ModuleLayoutRunType,
	ownerUserId: Number,
	processId: String,
	repetitionConfig: TaskRepetitionConfigRunType,
	responsibleUserId: Number,
	spoingButton: SpoingButtonRunType,
	statusButton: StatusButtonRunType,
	taskButton: TaskButtonRunType,
	title: String,
});

export type Task = Static<typeof TaskRunType>;

export const TaskFlatHistoryEntryRunType = Record({
	author: String,
	date: String,
	title: String,
});

export type TaskFlatHistoryEntry = Static<typeof TaskFlatHistoryEntryRunType>;
